import { useQuery } from '@apollo/client';
import { UserContext } from 'contexts/user/UserContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Route, RouteStop, RouteStopsConnection } from 'services/graphqltypes';
import { ROUTE_BY_SLUG, ROUTE_CURRENT_USER_RATINGS, ROUTE_USER_RATING_STATUS } from './queries';

/**
 * @typedef QuizProgressLocalStorageRoute
 * @property {string} routeId - The unique identifier of the route
 * @property {Array<{stopId: string, status: boolean, tries?: number}>} stopsStatus - Array of stop statuses
 * @property {number} [tries] - Optional number of attempts made at the stop
 */
export type QuizProgressLocalStorageRoute = {
  routeId: string,
  stopsStatus: {
    /* stopId: string - The unique identifier of the stop */
    stopId: string,
    /* status: boolean - Whether the user has completed the stop */
    status: boolean,
    /* tries?: number - Optional number of times user has completed the stop */
    tries?: number
  }[]
};
export type QuizProgressLocalStorage = QuizProgressLocalStorageRoute[];
export type RouteRatingsLocalStorage = { routeId: string, routeUserRatingId: string, claimToken: string }[];
const COMPLETED_RATING_CLAIMS_KEY = 'completedRouteRatingClaims';
const completedRatingClaimsInMemory = new Set<string>();

const readCompletedRatingClaimIds = (): Set<string> => {
  const completed = new Set(completedRatingClaimsInMemory);
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(COMPLETED_RATING_CLAIMS_KEY) ?? 'null');
    if (Array.isArray(stored)) stored.forEach((id) => {
      if (typeof id === 'string') completed.add(id);
    });
  } catch {
    // Keep the in-memory marker if session storage is unavailable.
  }
  return completed;
};

export const markCompletedRouteRatingClaims = (ids: string[]): void => {
  ids.forEach((id) => completedRatingClaimsInMemory.add(id));
  try {
    sessionStorage.setItem(COMPLETED_RATING_CLAIMS_KEY, JSON.stringify([...readCompletedRatingClaimIds()]));
  } catch {
    // The in-memory marker still prevents stale ratings returning in this tab.
  }
};

export const isPermanentlyRejectedRatingClaim = (error: unknown): boolean => {
  const graphQLErrors = (error as any)?.graphQLErrors;
  return Array.isArray(graphQLErrors) && graphQLErrors.some(
    (graphQLError) => graphQLError?.extensions?.exception?.code === 'P0002',
  );
};

// Keep the local marker while server status is unknown; clearing it during a
// transient query failure could let the guest submit a duplicate rating.
export const pendingStoredRating = <T extends RouteRatingsLocalStorage[number]>(
  rating: T | undefined,
  serverRating: { createdByUserId?: string | null } | null | undefined,
): T | undefined => serverRating === null || serverRating?.createdByUserId ? undefined : rating;

export const readStoredRouteRatings = (): RouteRatingsLocalStorage => {
  const ratingsById = new Map<string, RouteRatingsLocalStorage[number]>();
  const completedRatingClaims = readCompletedRatingClaimIds();
  for (const getStorage of [() => localStorage, () => sessionStorage]) {
    try {
      const storage = getStorage();
      const parsed: unknown = JSON.parse(storage.getItem('routeRatings') ?? 'null');
      if (Array.isArray(parsed)) parsed.forEach((rating) => {
        if (rating && typeof rating === 'object' && typeof rating.routeUserRatingId === 'string') {
          const savedRating = rating as RouteRatingsLocalStorage[number];
          const existing = ratingsById.get(savedRating.routeUserRatingId);
          if (!existing || (!existing.claimToken && savedRating.claimToken)) {
            ratingsById.set(savedRating.routeUserRatingId, savedRating);
          }
        }
      });
    } catch {
      // Ignore malformed or unavailable browser storage.
    }
  }
  return [...ratingsById.values()].filter((rating) => !completedRatingClaims.has(rating.routeUserRatingId));
};

export const writeStoredRouteRatings = (ratings: RouteRatingsLocalStorage): boolean => {
  const serializedRatings = JSON.stringify(ratings);
  try {
    localStorage.setItem('routeRatings', serializedRatings);
    try { sessionStorage.removeItem('routeRatings'); } catch {
      // The durable copy is already saved.
    }
    return true;
  } catch {
    try {
      sessionStorage.setItem('routeRatings', serializedRatings);
      return true;
    } catch { return false; }
  }
};

export interface RouteWithCompletedRouteStops extends Route {
  completedRouteStops: RouteStopsConnection;
}

/**
 * fetches data using the ROUTE_BY_SLUG and ROUTE_CURRENT_USER_RATINGS query and modifies the routeBySlug object based on the quizProgress and routeRatings data stored in localStorage if the user is not authenticated.
 * @param questSlug questSlug from the url
 * @returns an object with the route data and loading and error states
 */
function useLocalStorageRouteData(questSlug: string, refetchLocalStorage?: any) {
  const { isAuthenticated, currentUser } = useContext(UserContext);
  const { loading, error, data } = useQuery(ROUTE_BY_SLUG, {
    variables: {
      slug: questSlug,
    }
  });

  const { data: routeCurrentUserRatings } = useQuery(ROUTE_CURRENT_USER_RATINGS, {
    variables: {
      routeSlug: questSlug,
      userId: currentUser?.id
    },
    skip: !currentUser?.id
  });

  const [QuizProgressLocalStorage, setQuizProgressLocalStorage] = useState<QuizProgressLocalStorage>([]);
  const [RouteRatingsLocalStorage, setRouteRatingsLocalStorage] = useState<RouteRatingsLocalStorage>([]);

  const guestRatingForRoute = RouteRatingsLocalStorage.find(
    (record) => record.routeId === data?.routeBySlug?.id,
  );
  const { data: ratingStatus, loading: ratingStatusLoading } = useQuery(ROUTE_USER_RATING_STATUS, {
    variables: { id: guestRatingForRoute?.routeUserRatingId },
    skip: isAuthenticated || !guestRatingForRoute,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (refetchLocalStorage) {
      try {
        const progress: unknown = JSON.parse(localStorage.getItem('quizProgress') || '[]');
        setQuizProgressLocalStorage(Array.isArray(progress) ? progress as QuizProgressLocalStorage : []);
      } catch {
        setQuizProgressLocalStorage([]);
      }
      setRouteRatingsLocalStorage(readStoredRouteRatings());
    }
  }, [refetchLocalStorage]);

  const route = useMemo(() => {
    if (loading || error || (!!guestRatingForRoute && ratingStatusLoading)) return null;

    const routeBySlug = data.routeBySlug || {} as RouteWithCompletedRouteStops;

    // set stop status based on localStorage if user is not logged in
    if (!isAuthenticated) {
      const routeWithLocalStorageStatus = { ...routeBySlug };

      // update routeBySlug with quizProgress from localStorage
      const guestProgress = QuizProgressLocalStorage?.find((record) => record.routeId === routeWithLocalStorageStatus.id);
      const updatedCompletedRouteStops = {
        ...routeWithLocalStorageStatus.completedRouteStops,
        totalCount: guestProgress?.stopsStatus.filter((stop) => stop.status).length || 0
      }; // completedRouteStops is readonly, so we need to create a new object
      const updatedRouteStops = {
        ...routeWithLocalStorageStatus.routeStops,
        nodes: routeWithLocalStorageStatus.routeStops.nodes.map((routeStop: RouteStop) => {
          return {
            ...routeStop, currentUserHasCompleted: guestProgress?.stopsStatus.find(
              (stopStatus) => stopStatus.stopId === routeStop.id)?.status
          };
        })
      }; // routeStops is readonly, so we need to create a new object

      // update routeBySlug with routeRatings from localStorage
      const guestRatings = pendingStoredRating(guestRatingForRoute, ratingStatus?.routeUserRating);
      const updatedRouteRatings = {
        totalCount: guestRatings ? 1 : 0,
        routeUserRatingIdFromLocalStorage: guestRatings?.routeUserRatingId,
      };

      return {
        ...routeWithLocalStorageStatus,
        completedRouteStops: updatedCompletedRouteStops,
        routeStops: updatedRouteStops,
        routeUserRatings: updatedRouteRatings
      };
    } else {
      return { ...routeBySlug, routeUserRatings: routeCurrentUserRatings?.routeUserRatings };
    }
  }, [data, isAuthenticated, loading, error, QuizProgressLocalStorage, guestRatingForRoute, ratingStatus, ratingStatusLoading, routeCurrentUserRatings]);

  return { route, loading: loading || (!!guestRatingForRoute && ratingStatusLoading), error };
}

export default useLocalStorageRouteData;
